import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { FileDown, Loader2, TrendingUp, TrendingDown } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { generatePayslipPdf } from "@/lib/payslipPdf";

const MONTHS_FR = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];

export const PayslipDialog = ({ employee }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const now = new Date();
  const defaultMonth = now.getMonth() === 0 ? 12 : now.getMonth();
  const defaultYear = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
  const [month, setMonth] = useState(defaultMonth);
  const [year, setYear] = useState(defaultYear);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  const years = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 5; y--) years.push(y);

  // Preview payslip data (especially overtime) each time month/year changes
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.getEmployeePayslip(employee.id, { year, month });
        if (!cancelled) setPreview(data);
      } catch { /* non bloquant */ }
    })();
    return () => { cancelled = true; };
  }, [open, employee.id, year, month]);

  const handleGenerate = async () => {
    try {
      setLoading(true);
      const res = await api.getEmployeePayslip(employee.id, { year, month });
      generatePayslipPdf(res.data, { companyName: user?.name || "Bati Assistant" });
      toast.success("Fiche de paie PDF générée");
      setOpen(false);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Erreur génération fiche de paie";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        onClick={() => setOpen(true)}
        data-testid="open-payslip-dialog"
        title="Générer la fiche de paie mensuelle PDF"
      >
        <FileDown className="h-4 w-4 mr-2" />
        Fiche de paie
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="payslip-dialog">
          <DialogHeader>
            <DialogTitle>Fiche de paie mensuelle</DialogTitle>
            <DialogDescription>
              Agrège salaire de base, commissions vendeur, primes de petit déplacement,
              primes manuelles et retenues pour le mois sélectionné.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="space-y-1">
              <Label htmlFor="payslip-month">Mois</Label>
              <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v))}>
                <SelectTrigger id="payslip-month" data-testid="payslip-month-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MONTHS_FR.map((m, i) => (
                    <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="payslip-year">Année</Label>
              <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v))}>
                <SelectTrigger id="payslip-year" data-testid="payslip-year-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map((y) => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {preview?.overtime && (preview.overtime.overtime_hours > 0 || preview.overtime.deficit_hours > 0 || preview.overtime.month_delta_hours !== 0) && (
            <div className="rounded-lg border border-indigo-200 bg-indigo-50/60 p-3 space-y-2" data-testid="payslip-overtime-preview">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {preview.overtime.overtime_hours > 0
                    ? <TrendingUp className="h-4 w-4 text-emerald-600" />
                    : preview.overtime.deficit_hours > 0
                      ? <TrendingDown className="h-4 w-4 text-red-600" />
                      : null}
                  <span className="text-sm font-medium">Heures supplémentaires auto-calculées</span>
                </div>
                <Badge variant="outline" className="text-[10px] border-indigo-300 text-indigo-700">
                  Mode {preview.overtime.mode === "annual" ? "annuel" : "mensuel"}
                </Badge>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="p-2 rounded bg-white border">
                  <div className="text-[10px] text-muted-foreground">Delta du mois</div>
                  <div className="font-mono text-sm font-medium">
                    {preview.overtime.month_delta_hours >= 0 ? "+" : ""}
                    {Number(preview.overtime.month_delta_hours).toFixed(1)}h
                  </div>
                </div>
                <div className="p-2 rounded bg-emerald-50 border border-emerald-200">
                  <div className="text-[10px] text-emerald-700">Heures sup.</div>
                  <div className="font-mono text-sm font-semibold text-emerald-700">
                    {Number(preview.overtime.overtime_hours).toFixed(1)}h
                  </div>
                </div>
                <div className="p-2 rounded bg-red-50 border border-red-200">
                  <div className="text-[10px] text-red-700">Déficit</div>
                  <div className="font-mono text-sm font-semibold text-red-700">
                    {Number(preview.overtime.deficit_hours).toFixed(1)}h
                  </div>
                </div>
              </div>
              {preview.overtime.mode === "annual" && preview.overtime.overtime_hours === 0 && preview.overtime.deficit_hours === 0 && (
                <p className="text-[11px] text-muted-foreground italic">
                  Les heures sup sont réconciliées à la fin de la période annuelle (fiche de paie auto-remplie à ce moment-là).
                </p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={loading}>
              Annuler
            </Button>
            <Button
              onClick={handleGenerate}
              disabled={loading}
              data-testid="payslip-generate-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />}
              Générer le PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PayslipDialog;
